import type { StaffRole } from "@lafayette/auth";
import { matchRoutes } from "react-router";

import { rolesWithCapability } from "../access/capabilities";

// The staff console's OFFERING: which areas a staff member is offered, in one place. An area is declared once (`adminAreas`): its address,
// the roles that may open it, whether it is listed, and the feature it needs. The menu, the Home tiles, the route guard (`AreaGuard`), the
// breadcrumbs and every link to another area ask the functions below, so a rule is one edit and an area cannot be in the menu but open
// to everybody (or guarded but missing from the menu). Presentation only (ADR 0012): hiding an area never authorizes or refuses anything,
// the API decides every request again. This module does not route: React Router still owns the addresses.

/** The console features an environment switches on. The API says which (`features` of `GET /staff/attention`); the browser never guesses. */
export type ConsoleFeatures = Readonly<{ payments: boolean }>;
type ConsoleFeature = keyof ConsoleFeatures;

type AreaShape = Readonly<{
  key: string;
  label: string;
  path: string;
  allowedRoles: readonly StaffRole[];
  /** A hidden area: opens only while the API says this feature is on in this environment (CHT-767). */
  feature?: ConsoleFeature;
  /**
   * `false`: the area is not offered in the menu, the Home tiles or a breadcrumb link (CHT-1980). Its address still opens for the roles
   * above and is guarded (`AreaGuard`), and a link to it works. Used for an area whose own page is only a placeholder.
   */
  listed?: false;
}>;

const allStaff: readonly StaffRole[] = [
  "concierge",
  "supervisor",
  "catalog_editor",
  "administrator",
];

const declaredAreas = [
  // Home (CHT-767): what needs the staff member now. Every staff role has it: it lists only the attention sources the API allows that role,
  // and for a role with none (catalog editor, administrator) it shows the areas of the role.
  { key: "home", label: "Home", path: "/home", allowedRoles: allStaff },
  // The staff workspace (CHT-732). A staff user sees a conversation only as a concierge or a supervisor (contract section 10.3); the
  // catalog editor and the administrator hold no `handoff.read_internal`, so the API refuses every workspace route for them.
  {
    key: "concierge",
    label: "Concierge",
    path: "/concierge",
    allowedRoles: rolesWithCapability("handoff.read_internal"),
  },
  // Customer memory (CHT-721). `memory.read`, `memory.manage` and `customer.read_*` belong to the
  // concierge and the supervisor (domain `capabilityRoles`); the API refuses every customer route for any
  // other role, so the console does not offer the area to them.
  {
    key: "customers",
    label: "Customers",
    path: "/customers",
    allowedRoles: rolesWithCapability("memory.read"),
  },
  // Privacy (CHT-771): export and deletion requests of customers, under `/customers/privacy`. `privacy.*` is held by the supervisor and the
  // administrator. The administrator has no Customers area, so this entry is how they reach it.
  {
    key: "privacy",
    label: "Privacy",
    path: "/customers/privacy",
    allowedRoles: rolesWithCapability("privacy.read"),
  },
  {
    key: "requests",
    label: "Requests",
    path: "/requests",
    allowedRoles: rolesWithCapability("request.work"),
  },
  // Coverage (CHT-768): the cover of customers and who is away. `assignment.manage` is held by the supervisor alone.
  {
    key: "coverage",
    label: "Coverage",
    path: "/coverage",
    allowedRoles: rolesWithCapability("assignment.manage"),
  },
  // Trips and Actions (CHT-1980): their own pages are empty placeholders, so no role is offered them until real pages exist. `trips` stays
  // an entry because the real itinerary pages under `/trips/:tripId/itinerary` use it as their guard and their record link.
  { key: "trips", label: "Trips", path: "/trips", allowedRoles: allStaff, listed: false },
  { key: "actions", label: "Actions", path: "/actions", allowedRoles: allStaff, listed: false },
  // Payments (CHT-767 gate; the screens are CHT-759). A hidden entry: it shows only while the API says `payments` is on.
  // `payment.read` is held by the concierge and the supervisor; the administrator holds `payment.refund`. The API decides every call.
  {
    key: "payments",
    label: "Payments",
    path: "/payments",
    allowedRoles: rolesWithCapability("payment.read_console", "payment.refund"),
    feature: "payments",
  },
  // The administrator holds no `catalog.*` capability (domain `capabilityRoles`), so the API refuses
  // every catalog route for that role and the console must not offer it. A supervisor and a concierge
  // hold `catalog.read_internal` and see the catalog read-only; only the catalog editor edits.
  {
    key: "catalog",
    label: "Catalog",
    path: "/catalog",
    allowedRoles: rolesWithCapability("catalog.read_internal"),
  },
  // `catalog.import` is held by the catalog editor alone (domain `capabilityRoles`). The API refuses
  // every import route for any other role, so the console does not offer the Imports area to them.
  {
    key: "imports",
    label: "Imports",
    path: "/imports",
    allowedRoles: rolesWithCapability("catalog.import"),
  },
  {
    key: "operations",
    label: "Operations",
    path: "/operations",
    allowedRoles: rolesWithCapability("operations.jobs.read"),
  },
  {
    key: "staff",
    label: "Staff",
    path: "/staff",
    // `staff.manage_roles` and `staff.manage_status` (CHT-768): the team directory, the access review, roles and offboarding.
    allowedRoles: rolesWithCapability("staff.manage_roles"),
  },
  // Analytics (CHT-2027, ADR 0017): the numbers of ONE named customer. `analytics.read_customer` is held by the concierge, the supervisor and the
  // administrator. A concierge opens only the customers they cover; the server decides that, so the area is offered to the role and the
  // customer picker lists what the server allows.
  {
    key: "analytics",
    label: "Analytics",
    path: "/analytics",
    allowedRoles: rolesWithCapability("analytics.read_customer"),
  },
] as const satisfies readonly AreaShape[];
export type AreaKey = (typeof declaredAreas)[number]["key"];

/** One declared area. */
export type AdminArea = Readonly<Omit<AreaShape, "key"> & { key: AreaKey }>;

/** The areas in menu order. */
export const adminAreas: readonly AdminArea[] = declaredAreas;

export const NO_CONSOLE_FEATURES: ConsoleFeatures = { payments: false };

/** What decides the offering: the roles of the session and the features the API switched on. */
export type Offering = Readonly<{ roles: readonly StaffRole[]; features: ConsoleFeatures }>;

export function offeringOf(
  roles: readonly StaffRole[],
  features: ConsoleFeatures = NO_CONSOLE_FEATURES,
): Offering {
  return { roles, features };
}

const areasByKey: ReadonlyMap<AreaKey, AdminArea> = new Map(
  adminAreas.map((area) => [area.key, area]),
);

function areaOf(key: AreaKey): AdminArea {
  return areasByKey.get(key) as AdminArea;
}

/** The address of an area. A page links to the area through this, never by writing the path. */
export function areaPath(key: AreaKey): string {
  return areaOf(key).path;
}

/** Whether a role of the staff member allows the area. The route guard tells this refusal ("your role") from a switched-off feature. */
export function areaAllowsRoles(key: AreaKey, roles: readonly StaffRole[]): boolean {
  return areaOf(key).allowedRoles.some((allowed) => roles.includes(allowed));
}

/**
 * Whether the area opens for this staff member: a role allows it and, for an area behind a feature, the feature is on. This is the rule of
 * the route guard and of every link to the area (a link to an area that does not open would end in a refusal).
 */
export function isAreaOpenable(key: AreaKey, offering: Offering): boolean {
  const { feature } = areaOf(key);
  return (
    areaAllowsRoles(key, offering.roles) && (feature === undefined || offering.features[feature])
  );
}

/** Whether the menu, the Home tiles and the breadcrumbs offer the area: it opens, and it is listed. */
export function isAreaOffered(key: AreaKey, offering: Offering): boolean {
  return areaOf(key).listed !== false && isAreaOpenable(key, offering);
}

/** The areas a staff member is offered, in menu order. */
export function offeredAreas(offering: Offering): readonly AdminArea[] {
  return adminAreas.filter((area) => isAreaOffered(area.key, offering));
}

/**
 * The route patterns of the shell, each owned by one area. `AdminApplication` mounts its routes with these very patterns
 * (`shellRoutePath`), and `areaForPath` matches an address against them with the matcher of React Router itself. So the guard and the router
 * read the same patterns with the same rules (case, `%`-encoding, trailing and double slashes, the best match among several), and cannot
 * disagree about which area an address belongs to.
 */
const shellRouteTable = {
  home: { path: "/home", area: "home" },
  concierge: { path: "/concierge/*", area: "concierge" },
  customerPrivacy: { path: "/customers/privacy", area: "privacy" },
  customers: { path: "/customers/*", area: "customers" },
  analytics: { path: "/analytics/*", area: "analytics" },
  tripItinerary: { path: "/trips/:tripId/itinerary/*", area: "trips" },
  requests: { path: "/requests/*", area: "requests" },
  trips: { path: "/trips", area: "trips" },
  actions: { path: "/actions", area: "actions" },
  payments: { path: "/payments/*", area: "payments" },
  catalog: { path: "/catalog/*", area: "catalog" },
  imports: { path: "/imports/*", area: "imports" },
  operations: { path: "/operations/*", area: "operations" },
  staff: { path: "/staff", area: "staff" },
  coverage: { path: "/coverage/*", area: "coverage" },
} as const satisfies Readonly<Record<string, Readonly<{ path: string; area: AreaKey }>>>;

export type ShellRoute = keyof typeof shellRouteTable;

/** The pattern a route of the shell is mounted at. */
export function shellRoutePath(route: ShellRoute): string {
  return shellRouteTable[route].path;
}

/** Every pattern of the shell with its area, for a test that compares them with the router. */
export const shellRoutePatterns: readonly Readonly<{ path: string; area: AreaKey }>[] =
  Object.values(shellRouteTable);

const matchable = shellRoutePatterns.map(({ path, area }) => ({ path, id: area }));

/**
 * The area that owns an address, found the way the router finds the route (`/customers/privacy` is Privacy, not Customers; `/Customers/%70rivacy`
 * too). `undefined` for an address that no route of the shell owns.
 */
export function areaForPath(pathname: string): AdminArea | undefined {
  const owner = matchRoutes(matchable, pathname)?.at(-1)?.route.id;
  return owner === undefined ? undefined : areaOf(owner as AreaKey);
}

/** True for the address of an area that is not listed (`listed: false`): a breadcrumb must not link to it. */
export function isUnlistedAreaPath(path: string): boolean {
  return adminAreas.some((area) => area.listed === false && area.path === path);
}
